export function convertJsonArraysToJs(newObj) {
    if (Array.isArray(newObj)) {
        return newObj.map(item => convertJsonArraysToJs(item));
    }
    for (const key in newObj) {
        if (Object.prototype.hasOwnProperty.call(newObj, key)) {
            try {
                if (typeof newObj[key] === 'string' && (newObj[key].startsWith('[') || newObj[key].startsWith('{'))) {
                    const parsedValue = JSON.parse(newObj[key]);
                    if (Array.isArray(parsedValue)) {
                        newObj[key] = parsedValue;
                    }
                }
            } catch (error) {
                // Wenn das Parsen fehlschlägt, lassen Sie den Wert unverändert
            }
        }
    }
    return newObj;
}

export function reformatFloats(obj) {
    for (let key in obj) {
        if (typeof obj[key] === 'object') {
            reformatFloats(obj[key]);
        } else if (typeof obj[key] === 'string') {
            obj[key] = obj[key].replace(',', '.');
        }
    }
}

export function floatsToLocal(obj) {
    for (let key in obj) {
        if (typeof obj[key] === 'object') {
            floatsToLocal(obj[key]);
        } else if (typeof obj[key] === 'string' && isFloat(obj[key])) {
            // Komma durch Punkt ersetzen um parseFloat in eine gültige Zahl umzuwandeln
            let num = parseFloat(obj[key].replace(',', '.'));
            // Überprüfen ob Umwandlung erfolgreich war (nicht NaN) und die Zahl ist endlich
            if (!isNaN(num) && isFinite(num)) {
                // toLocaleString auf eine Zahl anwenden
                obj[key] = num.toLocaleString('de-DE', { useGrouping: false });
            }
        }
    }
}

export function isFloat(val) {
    const floatRegex = /^-?\d+(\.\d+)?$/;
    if (!floatRegex.test(val.replace(',', '.')))
        return false;

    return !isNaN(parseFloat(val));
}

/**
 * Generiert eine Token-basierte URL für ein Bild aus einem C-Learning Upload.
 * @param {Object} APIService - Der APIService für den Token-Request.
 * @param {string} fileId - Die ID der Datei.
 * @returns {Promise<string|null>} Die downloadbare URL oder null bei Fehler.
 */
export async function getFileTokenUrl(APIService, fileId) {
    try {
        const token = await APIService.getFileToken(fileId);
        if (token) {
            const serverURL = process.env.VUE_APP_ROOT_API || 'https://api.c-learning.de';
            return `${serverURL}/v1/files/download/${token}/`;
        }
    } catch (error) {
        console.error("Error generating file token URL:", error);
    }
    return null;
}

/**
 * Lädt eine Datei via /content/ Pfad mit Authorization Header und erzeugt eine Blob-URL.
 * @param {string} path - Der Pfad der Datei (item.path).
 * @param {string} token - Der Access-Token für den Authorization Header.
 * @returns {Promise<string|null>} Die Blob-URL oder null bei Fehler.
 */
export async function getAuthenticatedFileUrl(path, token) {
    try {
        const serverURL = process.env.VUE_APP_ROOT_API || 'https://api.c-learning.de';
        const url = `${serverURL}/v1/content/${path}`;
        
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (error) {
        console.error("Error fetching authenticated file:", error);
    }
    return null;
}

function sanitizeFilename(filename) {
    if (!filename) return '';
    let normalized = String(filename).trim();
    normalized = normalized.replace(/,(?=(png|jpe?g|gif|webp|pdf)$)/i, '.');
    return normalized;
}

function splitQueryAndHash(filename) {
    const source = String(filename);
    const q = source.indexOf('?');
    const h = source.indexOf('#');
    const cut =
        q === -1 ? h :
        h === -1 ? q :
        Math.min(q, h);
    if (cut === -1) return { path: source, suffix: '' };
    return { path: source.slice(0, cut), suffix: source.slice(cut) };
}

function swapExtension(path, nextExt) {
    if (!path || !nextExt) return path;
    return path.replace(/\.[^./]+$/i, `.${nextExt}`);
}

export function normalizeAssetFilename(filename, defaultExtension = '') {
    if (!filename) return '';
    const sanitized = sanitizeFilename(filename);
    if (/^(https?:\/\/|blob:|data:)/i.test(sanitized)) {
        return sanitized;
    }

    const { path, suffix } = splitQueryAndHash(sanitized);
    let resolvedPath = path;
    const hasExtension = /\.[^./]+$/.test(path);

    if (!hasExtension && defaultExtension) {
        resolvedPath = `${path}.${defaultExtension}`;
    }

    if (defaultExtension === 'png' && /\.bmp$/i.test(resolvedPath)) {
        resolvedPath = swapExtension(resolvedPath, 'png');
    }

    return `${resolvedPath}${suffix}`;
}

function basename(value) {
    const source = String(value || '');
    const cut = source.split(/[?#]/, 1)[0];
    const parts = cut.split('/');
    return parts[parts.length - 1] || '';
}

function removeExtension(value) {
    return String(value || '').replace(/\.[^.]+$/, '');
}

export function assetNamesMatch(candidateName, dbName, defaultExtension = '') {
    if (!candidateName || !dbName) return false;
    const left = basename(normalizeAssetFilename(candidateName, defaultExtension)).toLowerCase();
    const right = basename(normalizeAssetFilename(dbName, defaultExtension)).toLowerCase();
    if (left === right) return true;
    return removeExtension(left) === removeExtension(right);
}

/**
 * Baut die URL für ein Asset basierend auf der neuen hierarchischen Struktur.
 * @param {string} filename - Der Dateiname des Assets.
 * @param {string} toolboxNumber - Die Nummer der Toolbox.
 * @param {string} type - Der Typ des Unterordners ('wp' für Werkstück, 'cp' für Checkpoint).
 * @param {string|number} identifier - Die ID des Werkstücks oder die Nummer des Checkpoints.
 * @param {string} [defaultExtension] - Standard-Endung, falls der Dateiname keine Endung enthält.
 * @returns {string} Die vollständige URL zum Asset.
 */
export function getAssetUrl(filename, toolboxNumber, type, identifier, defaultExtension = '') {
    const ASSETS_URL = process.env.VUE_APP_ASSETS_URL || '';
    if (!filename) return '';
    const normalizedFilename = normalizeAssetFilename(filename, defaultExtension);
    
    // Falls der Dateiname bereits eine vollständige URL ist, gib sie direkt zurück.
    if (normalizedFilename.startsWith('http') || normalizedFilename.startsWith('blob:') || normalizedFilename.startsWith('data:')) {
        return normalizedFilename;
    }

    // Wenn Bezeichner fehlen, können wir den Pfad nicht korrekt bauen. 
    if (!toolboxNumber || !type || !identifier) {
        console.warn(`Missing parameters for getAssetUrl: toolboxNumber(${toolboxNumber}), type(${type}), identifier(${identifier}), filename: ${filename}`);
        // Wenn toolboxNumber fehlt, versuchen wir es mit dem Dateinamen direkt, 
        // aber das führt meist zu kaputten URLs wie vom User gemeldet.
        const baseUrl = ASSETS_URL.endsWith('/') ? ASSETS_URL.slice(0, -1) : ASSETS_URL;
        return `${baseUrl}/${normalizedFilename}`;
    }

    // Sicherstellen, dass identifier vorhanden ist und toolboxNumber
    const safeToolboxNumber = toolboxNumber || '0';
    const safeIdentifier = identifier || '0';

    // Toolbox-Identifier formatieren: tb-xxxx (4 Stellen)
    let tbDir = 'tb-' + String(safeToolboxNumber).padStart(4, '0');
    
    // Sub-Directory formatieren: direkt für workpieces, sonst cp-xxxx (4 Stellen)
    let subDir = '';
    if (type === 'wp') {
        subDir = String(safeIdentifier);
    } else {
        subDir = type + '-' + String(safeIdentifier).padStart(4, '0');
    }

    const baseUrl = ASSETS_URL.endsWith('/') ? ASSETS_URL.slice(0, -1) : ASSETS_URL;
    return `${baseUrl}/toolboxes/${tbDir}/${subDir}/${normalizedFilename}`;
}

function normalizeTagValue(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase();
}

function extractTagStrings(tagsValue) {
    if (!tagsValue) return [];

    if (Array.isArray(tagsValue)) {
        return tagsValue
            .map(tag => {
                if (typeof tag === 'string') return tag;
                if (tag && typeof tag === 'object') {
                    return tag.name || tag.title || tag.label || tag.tag || tag.value || '';
                }
                return '';
            })
            .filter(Boolean);
    }

    if (typeof tagsValue === 'string') {
        return tagsValue.split(/[;,]/).map(v => v.trim()).filter(Boolean);
    }

    return [];
}

export function hasMesskursTag(course) {
    const tags = extractTagStrings(course?.tags);
    return tags.some(tag => normalizeTagValue(tag) === 'messkurs');
}

export function hasMesskursComment(course) {
    const comment = course?.comment;
    if (!comment) return false;
    return /messkurs/i.test(String(comment));
}

export function isMesskursCourse(course) {
    return hasMesskursTag(course) || hasMesskursComment(course);
}
